import * as React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableFooter from '@mui/material/TableFooter';
import TablePagination from '@mui/material/TablePagination';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import AnimateButton from 'ui-component/extended/AnimateButton';
import TextField from '@mui/material/TextField';
import useMediaQuery from '@mui/material/useMediaQuery';
import Paper from '@mui/material/Paper';
import { TablePaginationActions } from '../../../components/Pagination/TablePaginationActions';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import BasicModal from '../../../components/Modal/BasicModal';
import DeleteIcon from '@mui/icons-material/Delete';
import ViewIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import ModalFilter from 'components/Modal/ModalFilter';
import { listCalendars, deleteCalendar } from 'services/calendar';
import MainCard from 'ui-component/cards/MainCard';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.grey[300],
        color: theme.palette.common.black,
        fontWeight: 600
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14
    }
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover
    },
    '&:last-child td, &:last-child th': {
        border: 0
    }
}));

const emptyPaginated = { data: [], total: 0 };

export default function GridCalendar() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const theme = useTheme();
    const matchDownSM = useMediaQuery(theme.breakpoints.down('md'));
    const page = useSelector((state) => state.calendar.page);
    const nameCalendar = useSelector((state) => state.calendar.nameCalendar);
    const rowsPerPage = useSelector((state) => state.calendar.rowsPerPage);

    const [calendars, setCalendars] = React.useState(emptyPaginated);
    const [feedback, setFeedback] = React.useState({ open: false, message: '', severity: 'success' });
    const [idDestroyCalendar, setIdDestroyCalendar] = React.useState('');
    const [openModal, setOpenModal] = React.useState(false);
    const [openModalFilter, setOpenModalFilter] = React.useState(false);

    const handleOpenDestroy = (idDestroy) => {
        setIdDestroyCalendar(idDestroy);
        setOpenModal(true);
    };
    const handleCloseModal = () => setOpenModal(false);
    const handleCloseModalFilter = () => setOpenModalFilter(false);

    const handleChangePage = (event, newPage) => {
        dispatch({ type: 'SET_PAGE_CALENDAR', payload: newPage });
    };
    const handleChangeRowsPerPage = (event) => {
        dispatch({ type: 'SET_ROWS_PER_PAGE_CALENDAR', payload: parseInt(event.target.value, 10) });
        dispatch({ type: 'SET_PAGE_CALENDAR', payload: 0 });
    };

    const parseListResponse = (resp) => {
        const raw = resp.data;
        return typeof raw === 'string' ? JSON.parse(raw) : raw;
    };

    const loadCalendars = React.useCallback(
        (apiPage1Based, perPage, title) => {
            const qtd = perPage;
            const cleanTitle = title != null && String(title).trim() !== '' ? String(title).trim() : undefined;
            return listCalendars(apiPage1Based, qtd, cleanTitle)
                .then((resp) => setCalendars(parseListResponse(resp) || emptyPaginated))
                .catch(() => {
                    setCalendars(emptyPaginated);
                    setFeedback({
                        open: true,
                        message: 'Não foi possível carregar os calendários.',
                        severity: 'error'
                    });
                });
        },
        []
    );

    React.useEffect(() => {
        const apiPage = page + 1;
        const title = nameCalendar && String(nameCalendar).trim() !== '' ? String(nameCalendar).trim() : undefined;
        loadCalendars(apiPage, rowsPerPage, title);
    }, [page, rowsPerPage, loadCalendars]);

    const deleteCalendarById = () => {
        handleCloseModal();
        deleteCalendar(idDestroyCalendar)
            .then((resp) => {
                const msg = resp.data?.success || resp.data?.message || 'Calendário excluído.';
                setFeedback({ open: true, message: typeof msg === 'string' ? msg : 'Calendário excluído.', severity: 'success' });
                setIdDestroyCalendar('');
                const apiPage = page + 1;
                const title = nameCalendar && String(nameCalendar).trim() !== '' ? String(nameCalendar).trim() : undefined;
                loadCalendars(apiPage, rowsPerPage, title);
            })
            .catch((e) => {
                const msg = e.response?.data?.error || e.response?.data?.message || 'Erro ao excluir.';
                setFeedback({ open: true, message: typeof msg === 'string' ? msg : 'Erro ao excluir.', severity: 'error' });
            });
    };

    const rows = calendars?.data ?? [];
    const total = calendars?.total ?? 0;

    return (
        <>
            <BasicModal
                open={openModal}
                title="Excluir calendário"
                handleClose={handleCloseModal}
                description="Tem certeza que deseja excluir este calendário? Os feriados associados serão removidos."
                onDelete={deleteCalendarById}
            />
            <ModalFilter
                open={openModalFilter}
                title="Filtrar calendários"
                handleClose={handleCloseModalFilter}
                content={
                    <>
                        <Grid container spacing={matchDownSM ? 0 : 2}>
                            <Grid item xs={12} sm={12} sx={{ marginTop: 1 }}>
                                <TextField
                                    fullWidth
                                    id="outlined-nameCalendar"
                                    type="text"
                                    label="Nome do calendário"
                                    value={nameCalendar}
                                    onChange={(e) => dispatch({ type: 'SET_NAMECALENDAR_CALENDAR_FILTER', payload: e.target.value })}
                                    name="nameCalendar"
                                />
                            </Grid>
                        </Grid>
                        <Grid container alignItems="center" justifyContent="center" sx={{ mt: 3 }} spacing={1}>
                            <Grid item>
                                <AnimateButton>
                                    <Button
                                        fullWidth
                                        size="large"
                                        variant="outlined"
                                        color="inherit"
                                        onClick={() => {
                                            dispatch({ type: 'SET_CLEAR_CALENDAR_FILTER' });
                                            dispatch({ type: 'SET_PAGE_CALENDAR', payload: 0 });
                                            setOpenModalFilter(false);
                                            loadCalendars(1, rowsPerPage, undefined);
                                        }}
                                    >
                                        Limpar
                                    </Button>
                                </AnimateButton>
                            </Grid>
                            <Grid item>
                                <AnimateButton>
                                    <Button
                                        fullWidth
                                        size="large"
                                        variant="contained"
                                        onClick={() => {
                                            dispatch({ type: 'SET_PAGE_CALENDAR', payload: 0 });
                                            setOpenModalFilter(false);
                                            const t =
                                                nameCalendar && String(nameCalendar).trim() !== ''
                                                    ? String(nameCalendar).trim()
                                                    : undefined;
                                            loadCalendars(1, rowsPerPage, t);
                                        }}
                                    >
                                        Buscar
                                    </Button>
                                </AnimateButton>
                            </Grid>
                        </Grid>
                    </>
                }
            />

            <MainCard sx={{ m: 2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
                    <Stack direction="row" spacing={1} alignItems="center">
                        <CalendarMonthIcon color="primary" fontSize="large" />
                        <Box>
                            <Typography variant="h4" color="primary.dark" fontWeight={600}>
                                Calendários
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Cadastre feriados por ano e use nos SLAs e cronogramas.
                            </Typography>
                        </Box>
                    </Stack>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Button variant="outlined" startIcon={<FilterListIcon />} onClick={() => setOpenModalFilter(true)}>
                            Filtros
                        </Button>
                        <Button variant="contained" startIcon={<AddIcon />} component={Link} to="/novo_calendario">
                            Novo calendário
                        </Button>
                    </Stack>
                </Stack>

                <Snackbar
                    open={feedback.open}
                    autoHideDuration={6000}
                    onClose={() => setFeedback((f) => ({ ...f, open: false }))}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert severity={feedback.severity} onClose={() => setFeedback((f) => ({ ...f, open: false }))}>
                        {feedback.message}
                    </Alert>
                </Snackbar>

                <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                    <Table sx={{ minWidth: 480 }} aria-label="tabela de calendários">
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>Nome</StyledTableCell>
                                <StyledTableCell align="right">Ações</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.length === 0 && (
                                <StyledTableRow>
                                    <TableCell colSpan={2}>
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                            Nenhum calendário encontrado.
                                        </Typography>
                                    </TableCell>
                                </StyledTableRow>
                            )}
                            {rows.map((row) => (
                                <StyledTableRow key={row.id} hover>
                                    <StyledTableCell>{row.nome}</StyledTableCell>
                                    <StyledTableCell align="right">
                                        <Tooltip title="Visualizar">
                                            <IconButton size="small" onClick={() => navigate(`/calendario/${row.id}/view`)}>
                                                <ViewIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Editar">
                                            <IconButton size="small" onClick={() => navigate(`/calendario/${row.id}/edit`)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Excluir">
                                            <IconButton size="small" color="error" onClick={() => handleOpenDestroy(row.id)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </StyledTableCell>
                                </StyledTableRow>
                            ))}
                        </TableBody>
                        <TableFooter>
                            <TableRow>
                                <TablePagination
                                    rowsPerPageOptions={[5, 10, 25, { label: 'Todos', value: -1 }]}
                                    colSpan={2}
                                    count={total}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    labelRowsPerPage="Por página"
                                    labelDisplayedRows={({ from, to, count: c }) => `${from}-${to} de ${c !== -1 ? c : `${to}+`}`}
                                    SelectProps={{
                                        inputProps: { 'aria-label': 'Registros por página' },
                                        native: true
                                    }}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                    ActionsComponent={TablePaginationActions}
                                />
                            </TableRow>
                        </TableFooter>
                    </Table>
                </TableContainer>
            </MainCard>
        </>
    );
}
